// src/components/AiModal/AiModal.tsx
import { useState, useEffect, useContext, useMemo } from "react";
import { AppContext } from "../../App";
import WaveyIcon from "../Icons/waveyIcon";
import { Send, Loader2, X } from "lucide-react";
import axios from "axios";
import { useToast } from "../ToastProvider/ToastProvider";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:5000";

interface AiModalProps {
  open: boolean;
  onClose: () => void;
}

interface AIUsage {
  tier: "free" | "standard" | "premium";
  limit: number;
  remaining: number;
  used: number;
  resetTime: string;
  model: string;
}

interface IncomeSource {
  id?: string;
  name?: string;
  amount1?: number;
  amount2?: number;
  amount3?: number;
  amount4?: number;
  frequency?: "weekly" | "biweekly" | "monthly" | "one-time";
  color?: string;
}

export default function AiModal({ open, onClose }: AiModalProps) {
  const context = useContext(AppContext);
  const toast = useToast();

  if (!context) return null;

  // try to read new shape first, otherwise fallback to old keys
  const {
    expenses,
    // new preferred:
    waveAmounts: ctxWaveAmounts,
    incomeSources: ctxIncomeSources,
    // older single-value fields possibly still present:
    waveAmount: ctxWaveAmount,
    wave1Amount: ctxWave1Amount,
    wave2Amount: ctxWave2Amount,
    wave3Amount: ctxWave3Amount,
    wave4Amount: ctxWave4Amount,
    expenseListFrequency,
    language,
    t,
    token,
  } = context as any;

  // determine waveCount from frequency
  const waveCount = useMemo(() => {
    if (expenseListFrequency === "weekly") return 4;
    if (expenseListFrequency === "biweekly") return 2;
    return 1;
  }, [expenseListFrequency]);

  // NORMALIZE waveAmounts into an array length 4 (or at least waveCount)
  const waveAmounts: number[] = useMemo(() => {
    // prefer explicit array in context
    if (Array.isArray(ctxWaveAmounts) && ctxWaveAmounts.length > 0) {
      // ensure length >= waveCount, fill missing with 0
      const arr = [...ctxWaveAmounts];
      while (arr.length < waveCount) arr.push(0);
      return arr.slice(0, 4); // keep up to 4
    }

    // if individual waveN amounts exist, build array
    if (
      typeof ctxWave1Amount === "number" ||
      typeof ctxWave2Amount === "number" ||
      typeof ctxWave3Amount === "number" ||
      typeof ctxWave4Amount === "number"
    ) {
      return [
        ctxWave1Amount ?? 0,
        ctxWave2Amount ?? 0,
        ctxWave3Amount ?? 0,
        ctxWave4Amount ?? 0,
      ];
    }

    // if single waveAmount exists, replicate to match waveCount
    if (typeof ctxWaveAmount === "number") {
      return [ctxWaveAmount, ctxWaveAmount, ctxWaveAmount, ctxWaveAmount];
    }

    // fallback to zeros
    return [0, 0, 0, 0];
  }, [
    ctxWaveAmounts,
    ctxWave1Amount,
    ctxWave2Amount,
    ctxWave3Amount,
    ctxWave4Amount,
    ctxWaveAmount,
    waveCount,
  ]);

  // Normalized incomeSources
  const incomeSources: IncomeSource[] = useMemo(() => {
    if (Array.isArray(ctxIncomeSources)) return ctxIncomeSources;
    return [];
  }, [ctxIncomeSources]);

  // derive waveExpenses (Wave + Paycheck categories)
  const waveExpenses = useMemo(
    () => expenses?.filter((e: any) => e.category === "Wave" || e.category === "Paycheck") ?? [],
    [expenses]
  );

  // waveTotals per wave
  const waveTotals = useMemo(() => {
    return Array.from({ length: waveCount }, (_, i) => {
      const waveNum = i + 1;
      return waveExpenses
        .filter((e: any) => e.wave === waveNum)
        .reduce((sum: number, e: any) => sum + (e.price ?? 0), 0);
    });
  }, [waveExpenses, waveCount]);

  // waveBalances per wave (use corresponding waveAmounts)
  const waveBalances = useMemo(
    () => waveTotals.map((total, i) => (waveAmounts[i] ?? 0) - total),
    [waveTotals, waveAmounts]
  );

  const otherExpenses = useMemo(
    () => (expenses ?? []).filter((e: any) => e.category === "Other"),
    [expenses]
  );
  const unpaidOther = otherExpenses.filter((e: any) => !e.isPaid);
  const otherExpensesTotal = unpaidOther.reduce((s: number, e: any) => s + (e.price ?? 0), 0);

  // compute monthly totals for incomeSources
  const incomeSourcesSummary = useMemo(() => {
    return incomeSources.map((src) => {
      const frequency = src.frequency ?? "monthly";
      const monthly =
        frequency === "weekly"
          ? (src.amount1 ?? 0) + (src.amount2 ?? 0) + (src.amount3 ?? 0) + (src.amount4 ?? 0)
          : frequency === "biweekly"
          ? (src.amount1 ?? 0) + (src.amount2 ?? 0)
          : src.amount1 ?? 0;
      return { ...src, monthlyTotal: monthly };
    });
  }, [incomeSources]);

  // Total wave income (sum of the first waveCount waveAmounts)
  const totalWaveIncome = useMemo(
    () => waveAmounts.slice(0, waveCount).reduce((s, a) => s + (a ?? 0), 0),
    [waveAmounts, waveCount]
  );

  // Helper: build a readable financial context string to send to AI
  const getFinancialContext = () => {
    const top5 = (expenses ?? [])
      .slice()
      .sort((a: any, b: any) => (b.price ?? 0) - (a.price ?? 0))
      .slice(0, 5)
      .map((e: any) => `- ${e.name}: ${(e.price ?? 0).toFixed(2)} (${e.isPaid ? "Paid" : "Unpaid"}) - ${e.category === "Wave" || e.category === "Paycheck" ? `Wave ${e.wave}` : "Other"}`)
      .join("\n");

    const waveBreakdown = Array.from({ length: waveCount }, (_, i) => {
      const waveNum = i + 1;
      const waveExps = waveExpenses.filter((e: any) => e.wave === waveNum);
      const total = waveExps.reduce((s: number, e: any) => s + (e.price ?? 0), 0);
      const unpaid = waveExps.filter((e: any) => !e.isPaid).reduce((s: number, e: any) => s + (e.price ?? 0), 0);
      return `Wave ${waveNum}: ${total.toFixed(2)} total, ${unpaid.toFixed(2)} unpaid (${waveExps.length} expenses)`;
    }).join("\n");

    const incomeLines = incomeSourcesSummary.length
      ? `Income Sources:\n${incomeSourcesSummary.map((src) => {
          const waves = [src.amount1 ?? 0, src.amount2 ?? 0, src.amount3 ?? 0, src.amount4 ?? 0]
            .slice(0, src.frequency === "weekly" ? 4 : src.frequency === "biweekly" ? 2 : 1)
            .map((a, i) => `W${i + 1}: ${a.toFixed(2)}`)
            .join(" / ");
          return `- ${src.name ?? "Unnamed"} (${src.frequency ?? "monthly"}): ${waves} — Monthly est: ${((src.monthlyTotal ?? 0)).toFixed(2)}`;
        }).join("\n")}`
      : "No additional income sources.";

    return `
Financial Overview:
- Wave budgets: ${waveAmounts.slice(0, waveCount).map((a,i) => `Wave ${i+1}: ${a.toFixed(2)}`).join(" | ")}
- Payment Frequency: ${expenseListFrequency}
- Total Waves: ${waveCount}
- Total Wave Income (est): ${totalWaveIncome.toFixed(2)}

${incomeLines}

Wave Expenses Breakdown:
${waveBreakdown}

Other Expenses: ${otherExpensesTotal.toFixed(2)} unpaid (${unpaidOther.length} items)
Total Unpaid Wave Expenses: ${waveExpenses.filter((e: any) => !e.isPaid).reduce((s: number, e: any) => s + (e.price ?? 0), 0).toFixed(2)}
Balance Available (sum of per-wave balances): ${waveBalances.reduce((s, b) => s + b, 0).toFixed(2)}

Top 5 Expenses:
${top5}
    `.trim();
  };

  // AI usage & chat state
  const [aiMessages, setAiMessages] = useState<Array<{ role: "user" | "assistant"; content: string }>>([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [aiUsage, setAiUsage] = useState<AIUsage | null>(null);

  useEffect(() => {
    if (open && token) {
      (async () => {
        try {
          const resp = await axios.get(`${API_URL}/ai-advisor/usage`, { headers: { Authorization: `Bearer ${token}` } });
          setAiUsage(resp.data);
        } catch (err) {
          console.error("Failed to fetch AI usage:", err);
        }
      })();
    }
  }, [open, token]);

  const handleAiQuestion = async () => {
    if (!aiInput.trim() || !token) return;

    const userMessage = aiInput.trim();
    setAiInput("");
    setAiMessages((p) => [...p, { role: "user", content: userMessage }]);
    setAiLoading(true);

    try {
      const contextStr = getFinancialContext();

      const resp = await axios.post(
        `${API_URL}/ai-advisor`,
        { question: userMessage, context: contextStr },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const aiResponse = resp.data?.response ?? "No response.";
      const usage = resp.data?.usage;

      setAiMessages((p) => [...p, { role: "assistant", content: aiResponse }]);

      if (usage) {
        setAiUsage((prev) => prev ? { ...prev, ...usage } : { ...usage });
        if (usage.remaining <= 2 && usage.remaining > 0) {
          toast.warning(language === "en" ? `⚠️ Only ${usage.remaining} AI requests remaining today` : `⚠️ Solo ${usage.remaining} consultas de IA restantes hoy`);
        }
      }
    } catch (error: any) {
      console.error("AI Error:", error);
      let msg = t?.aiAdvisorError ?? "AI service error.";
      if (error.response?.status === 429) msg = error.response?.data?.error ?? msg;
      if (error.response?.data?.error) msg = error.response.data.error;
      setAiMessages((p) => [...p, { role: "assistant", content: msg }]);
    } finally {
      setAiLoading(false);
    }
  };

  const getTierBadge = () => {
    if (!aiUsage) return null;
    const colorMap: Record<string, string> = { free: "#8E8E93", standard: "#007AFF", premium: "#AF52DE" };
    const labelMap: Record<string, string> = { free: language === "en" ? "FREE" : "GRATIS", standard: language === "en" ? "STANDARD" : "ESTÁNDAR", premium: "PREMIUM" };
    return (
      <span style={{
        padding: "4px 8px",
        borderRadius: "6px",
        fontSize: "0.75rem",
        fontWeight: 600,
        backgroundColor: `${(colorMap as any)[aiUsage.tier]}20`,
        color: (colorMap as any)[aiUsage.tier],
        marginLeft: 8,
      }}>
        {labelMap[aiUsage.tier]}
      </span>
    );
  };

  if (!open) return null;

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
    >
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "90%",
          maxWidth: "600px",
          maxHeight: "80vh",
          background: "var(--card-bg)",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
          padding: "1.5rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "1rem",
          paddingBottom: "1rem",
          borderBottom: "1px solid var(--input-border)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <WaveyIcon style={{ color: "var(--primary-color)", width: 24, height: 24 }} />
            <h2 style={{ margin: 0, color: "var(--text-color)" }}>{t?.aiAdvisor ?? "AI Advisor"}</h2>
            {getTierBadge()}
          </div>
          <button
            onClick={onClose}
            aria-label={t?.closeAdvisor ?? "Close"}
            style={{
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0.5rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={24} style={{ color: "var(--text-color)" }} />
          </button>
        </div>

        {aiUsage && (
          <div style={{
            padding: "0.75rem",
            backgroundColor: "var(--input-bg)",
            borderRadius: 8,
            marginBottom: "1rem",
            fontSize: "0.85rem",
            color: "var(--input-placeholder)",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span>{language === "en" ? t.aiModal.requestsToday : t.aiModal.requestsTodayEs}</span>
              <span style={{ fontWeight: 600, color: "var(--text-color)" }}>{aiUsage.used} / {aiUsage.limit === Infinity ? "∞" : aiUsage.limit}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span>{language === "en" ? t.aiModal.remaining : t.aiModal.remainingEs}</span>
              <span style={{ fontWeight: 600, color: (aiUsage.remaining as number) <= 2 ? "#FF3B30" : "#34C759" }}>{aiUsage.remaining === Infinity ? "∞" : aiUsage.remaining}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>{language === "en" ? t.aiModal.model : t.aiModal.modelEs}</span>
              <span style={{ fontWeight: 600, color: "var(--text-color)" }}>{aiUsage.model}</span>
            </div>
          </div>
        )}

        <div style={{
          flex: 1,
          overflowY: "auto",
          marginBottom: "1rem",
          padding: "1rem",
          backgroundColor: "var(--input-bg)",
          borderRadius: 8,
          minHeight: 300,
          maxHeight: 400,
        }}>
          {aiMessages.length === 0 ? (
            <div style={{ color: "var(--input-placeholder)", textAlign: "center", padding: "2rem" }}>
              <WaveyIcon style={{ fontSize: 48, opacity: 0.3, marginBottom: '1rem', display: 'block', margin: '0 auto 1rem' }} />
              <p>{language === "en" ? t.aiModal.ask : t.aiModal.askEs}</p>
              {aiUsage && <p style={{ fontSize: "0.85rem", marginTop: "1rem" }}>                {language === "en" ? `${t.aiModal.poweredBy} ${aiUsage.model}` : `${t.aiModal.poweredByEs} ${aiUsage.model}`}</p>}
            </div>
          ) : (
            aiMessages.map((msg, idx) => (
              <div key={idx} style={{
                marginBottom: "1rem",
                padding: "0.75rem",
                borderRadius: 8,
                backgroundColor: msg.role === "user" ? "var(--primary-color)" : "var(--card-bg)",
                color: msg.role === "user" ? "#fff" : "var(--text-color)",
                marginLeft: msg.role === "user" ? "20%" : 0,
                marginRight: msg.role === "user" ? 0 : "20%",
              }}>
                <strong style={{ display: "block", marginBottom: "0.5rem" }}>{msg.role === "user" ? (language === "en" ? t.aiModal.you : t.aiModal.youEs) : "AI"}:</strong>
                <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>{msg.content}</div>
              </div>
            ))
          )}
          {aiLoading && (
            <div style={{ textAlign: "center", color: "var(--input-placeholder)", padding: "1rem" }}>
              <Loader2 size={24} className="animate-spin" />
              <p style={{ marginTop: "0.5rem" }}>{t?.aiAdvisorThinking ?? "Thinking..."}</p>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            type="text"
            className="input"
            placeholder={t?.aiAdvisorPlaceholder ?? "Ask a question..."}
            value={aiInput}
            onChange={(e) => setAiInput(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && !aiLoading && handleAiQuestion()}
            disabled={aiLoading || (aiUsage?.remaining === 0)}
            style={{ flex: 1 }}
          />
          <button
            onClick={handleAiQuestion}
            disabled={aiLoading || !aiInput.trim() || (aiUsage?.remaining === 0)}
            aria-label={t?.sendMessage ?? "Send"}
            style={{
              background: aiLoading || !aiInput.trim() || (aiUsage?.remaining === 0) ? "var(--input-border)" : "var(--primary-color)",
              border: "none",
              borderRadius: 8,
              padding: "0.5rem",
              cursor: aiLoading || !aiInput.trim() || (aiUsage?.remaining === 0) ? "not-allowed" : "pointer",
              color: aiLoading || !aiInput.trim() || (aiUsage?.remaining === 0) ? "var(--input-placeholder)" : "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Send size={20} />
          </button>
        </div>
      </div>
    </div>
  );
}
